import radarSchemaTemplate from './radarSchema.html';
import './radarSchema.scss';

export default angular
  .module('main.devices.edit.components.radarSchema', [])

  .directive('radarSchema', () => {
    return {
      restrict: 'E',
      template: radarSchemaTemplate,
      replace: true,
      scope: {
        loading: '=',
        mode: '=', // 'list' | 'subregion' | 'room'

        readonly: '=',
        onSubregionChange: '&',

        roomBoundariesExternal: '=roomBoundaries',
        selectedSubregionExternal: '=selectedSubregion'
      },
      link: (scope, element) => {
        const ro = new ResizeObserver(() => {
          scope.$applyAsync(() => {
            onWindowResize();
          });
        });
        ro.observe(element[0]);
        scope.$on('$destroy', () => {
          ro.disconnect();
        });

        onWindowResize();

        function onWindowResize() {
          // NOTE We use em units to make sizing relative to root element size.
          // So when we change root element size - everything is resized accordingly
          // Now real-life 1 cm (centimeter) = 1 px, and the FOV width is always 400cm
          const width = $(element).outerWidth();
          scope.rootFontSize = width / scope.fovWidth;
        }
      },
      controller: (
        $scope,
        $q,
        EditDeviceDataProvider,
        utils,
        $document,
        $window,
        $element,
        $log
      ) => {
        const blindSpotWidth = 0.3; // meters

        const fovSize = {
          wall: {
            width: 600,
            height: 400,
          },
          ceiling: {
            width: 400,
            height: 600,
          },
          corner: {
            width: 600,
            height: 600,
          },
        };

        let idIncrementor = 0; // ID counter for svg dimension lines text elements
        let isDragging = false;
        let dndOriginalX;
        let dndOriginalY;
        let selectedSubregionOriginalX;
        let selectedSubregionOriginalY;

        $scope.dataProvider = EditDeviceDataProvider.instance;

        $scope.error = false;

        /**
         * Radar mounting type
         * @type {'wall' | 'wall45' | 'ceiling' | 'ceiling45' | 'corner'}
         */
        $scope.mountingType = undefined;

        /**
         * Radar mounting type simplified
         * @type {'wall' | 'ceiling' | 'corner'}
         */
        $scope.mountingMethod = undefined;

        $scope.fovWidth = fovSize.wall.width;
        $scope.fovHeight = fovSize.wall.height;

        /**
         * Room boundaries in schema coordinates
         * @type {{x: number, y: number, width: number, height: number, invalid: boolean}}
         */
        $scope.roomBoundaries = { x: 0, y: 0, width: 0, height: 0, invalid: false };

        /**
         * FOV zone
         * @type {{x: number, y: number, width: number, height: number}}
         */
        $scope.fov = { x: 0, y: 0, width: 0, height: 0 };

        /**
         * FOV zone valid for placing subregions
         * @type {{x: number, y: number, width: number, height: number}}
         */
        $scope.fovValidZone = { x: 0, y: 0, width: 0, height: 0 };

        /**
         * Dimension Lines
         * @type {{id: string, x1: number, y1: number, x2: number, y2: number, label: string, length: number, [hidden]: boolean}[]}
         */
        $scope.dimensionLines = [];

        angular.extend($scope, {
          onSubregionClick: onSubregionClick,
          onRootClick: onRootClick,
          onRoomClick: onRoomClick,
          onSubregionMouseDown: onSubregionMouseDown,
        });

        init();

        // ----------------------------------------------

        function init() {
          $scope.dataProvider.locationStateRegionsResource.init();
          $scope.dataProvider.locationStateRegionBehaviorsResource.init();
          $scope.dataProvider.userSystemOfMeasurementResource.init();

          $scope.$watchGroup(
            [
              'roomBoundariesExternal',
              'selectedSubregionExternal',
              'dataProvider.locationStateRegionsResource.value',
              'dataProvider.locationStateRegionBehaviorsResource.value',
              'dataProvider.userSystemOfMeasurementResource.value'
            ],
            () => {
              if (!!$scope.dataProvider.locationStateRegionBehaviorsResource.value && !!$scope.roomBoundariesExternal) {
                // If we are in room/subregion mode, we don't need to have subregions as mandatory
                if (($scope.mode === 'room' || $scope.mode === 'subregion') || !!$scope.dataProvider.locationStateRegionsResource.value) {
                  $scope.systemOfMeasurement = $scope.dataProvider.userSystemOfMeasurementResource.value;
                  initViewModel();
                }
              }
            },
            true
          );

          $document.on('mouseup', onMouseUp);
          $document.on('mousemove', onMouseMove);
          $scope.$on('$destroy', () => {
            $document.off('mouseup', onMouseUp);
            $document.off('mousemove', onMouseMove);
          });

        }

        function initViewModel() {
          $scope.regionBehaviors = angular.copy($scope.dataProvider.locationStateRegionBehaviorsResource.value) || [];
          const roomBoundariesExternal = angular.copy($scope.roomBoundariesExternal);
          $scope.subregions = angular.copy($scope.dataProvider.locationStateRegionsResource.value) || [];
          $scope.selectedSubregion = angular.copy($scope.selectedSubregionExternal);

          if ($scope.selectedSubregion) {
            // apply changes from selected subregion to main subregions list
            const selectedSubregionIndex = $scope.subregions.findIndex(s => s.unique_id === $scope.selectedSubregion.unique_id);
            if (selectedSubregionIndex >= 0) {
              $scope.subregions[selectedSubregionIndex] = $scope.selectedSubregion;
            } else {
              $scope.subregions.push($scope.selectedSubregion);
            }
          }

          // Validate roomBoundaries
          if (
            !roomBoundariesExternal ||
            roomBoundariesExternal.x_min_meters == null ||
            roomBoundariesExternal.x_max_meters == null ||
            roomBoundariesExternal.y_min_meters == null ||
            roomBoundariesExternal.y_max_meters == null
          ) {
            $scope.error = true;
            return;
          }

          $scope.mountingType = {
            0: 'wall',
            3: 'wall45',
            1: 'ceiling',
            2: 'ceiling45',
            4: 'corner'
          }[roomBoundariesExternal.mounting_type];

          if (!$scope.mountingType) {
            $log.error('Unknown mounting method!');
            $scope.error = true;
            return;
          }

          if ($scope.mountingType.includes('wall')) {
            $scope.mountingMethod = 'wall';
          } else if ($scope.mountingType.includes('ceiling')) {
            $scope.mountingMethod = 'ceiling';
          } else if ($scope.mountingType.includes('corner')) {
            $scope.mountingMethod = 'corner';
          } else {
            $log.error('Unknown mounting method!');
            $scope.error = true;
            return;
          }

          $scope.fovWidth = fovSize[$scope.mountingMethod].width;
          $scope.fovHeight = fovSize[$scope.mountingMethod].height;

          $scope.fov = {
            x: 0,
            y: 0,
            width: $scope.fovWidth,
            height: $scope.fovHeight
          };
          $scope.fovValidZone = { // FoV except blind spot
            ...$scope.fov,
            height: $scope.mountingMethod === 'wall'
              ? $scope.fovHeight - transformUnits(blindSpotWidth) // blind spot for wall mounting
              : $scope.fovHeight
          };

          // Room Boundaries
          const radarOrigin = getRadarOrigin();
          const roomCoords = transformCoordinatesAndUnits(
            Math[radarOrigin.multiplierX > 0
              ? 'min'
              : 'max'](roomBoundariesExternal.x_min_meters, roomBoundariesExternal.x_max_meters),
            Math[radarOrigin.multiplierY > 0
              ? 'min'
              : 'max'](roomBoundariesExternal.y_min_meters, roomBoundariesExternal.y_max_meters)
          );
          $scope.roomBoundaries = {
            width: transformUnits(Math.abs(roomBoundariesExternal.x_max_meters - roomBoundariesExternal.x_min_meters)),
            height: transformUnits(Math.abs(roomBoundariesExternal.y_max_meters - roomBoundariesExternal.y_min_meters)),
            x: roomCoords.x,
            y: roomCoords.y,
            invalid: false
          };
          $scope.roomBoundaries.invalid = !isRectangleContained($scope.roomBoundaries, $scope.fovValidZone);

          // Subregions
          $scope.subregions
            .forEach((subregion) => {
              if (
                subregion.x_min_meters == null ||
                subregion.x_max_meters == null ||
                subregion.y_min_meters == null ||
                subregion.y_max_meters == null
              ) {
                subregion.invalid = true;
                subregion.error = true;
                return;
              }

              subregion.width = transformUnits(Math.abs(subregion.x_max_meters - subregion.x_min_meters));
              subregion.height = transformUnits(Math.abs(subregion.y_max_meters - subregion.y_min_meters));

              const subregionCoords = transformCoordinatesAndUnits(
                Math[radarOrigin.multiplierX > 0
                  ? 'min'
                  : 'max'](subregion.x_min_meters, subregion.x_max_meters),
                Math[radarOrigin.multiplierY > 0
                  ? 'min'
                  : 'max'](subregion.y_min_meters, subregion.y_max_meters)
              );
              subregion.x = subregionCoords.x;
              subregion.y = subregionCoords.y;

              subregion.context = $scope.regionBehaviors?.find(context => context.context_id === subregion.context_id);
            });

          $scope.subregions
            .forEach((subregion) => {
              validateSubregion(subregion);
            });

          processDimensionLines();

          const width = $($element).outerWidth();
          $scope.rootFontSize = width / $scope.fovWidth;
        }

        function validateSubregion(subregion) {
          subregion.invalid = $scope.subregions.some(s => s !== subregion && isSubregionsIntersect(s, subregion))
            || !isRectangleContained(subregion, $scope.roomBoundaries)
            || !isRectangleContained(subregion, $scope.fovValidZone);
        }

        /**
         * Transform Radar units (meters) into schema units (cm)
         * @param value meters
         * @returns {number} rounded centimeters
         */
        function transformUnits(value) {
          return Math.round(value * 10000) / 100;
        }

        /**
         * Transform Radar coordinates and units (meters) into schema coordinates and units (cm)
         * @param {number} x x coord in meters
         * @param {number} y y coord in meters
         * @returns {{x: number, y: number}} coords in rounded centimeters
         */
        function transformCoordinatesAndUnits(x, y) {
          const radarOrigin = getRadarOrigin();
          return {
            x: radarOrigin.x + radarOrigin.multiplierX * transformUnits(x),
            y: radarOrigin.y + radarOrigin.multiplierY * transformUnits(y),
          };
        }

        /**
         * Get radar coordinates system origin
         * @returns {{x: number, y: number, multiplierX: number, multiplierY: number}}
         */
        function getRadarOrigin() {
          let radarOriginX = 0;
          let radarOriginY = 0;
          let multiplierX = 1;
          let multiplierY = -1; // y-axis is inverted, because schema y-axis goes from top to down
          if ($scope.mountingMethod === 'wall') {
            radarOriginX = $scope.fovWidth / 2;
            radarOriginY = $scope.fovHeight;
            multiplierX = 1;
            multiplierY = -1;
          } else if ($scope.mountingMethod === 'ceiling') {
            radarOriginX = $scope.fovWidth / 2;
            radarOriginY = $scope.fovHeight / 2;
            multiplierX = 1;
            multiplierY = -1;
          } else if ($scope.mountingMethod === 'corner') {
            radarOriginX = $scope.fovWidth;
            radarOriginY = $scope.fovHeight;
            multiplierX = -1; // x-axis is inverted for corner mounting
            multiplierY = -1;
          }
          return {
            x: radarOriginX,
            y: radarOriginY,
            multiplierX: multiplierX,
            multiplierY: multiplierY
          };
        }

        function isSubregionsIntersect(subregion1, subregion2) {
          const rect1Left = subregion1.x;
          const rect1Right = subregion1.x + subregion1.width;
          const rect1Top = subregion1.y;
          const rect1Bottom = subregion1.y + subregion1.height;

          const rect2Left = subregion2.x;
          const rect2Right = subregion2.x + subregion2.width;
          const rect2Top = subregion2.y;
          const rect2Bottom = subregion2.y + subregion2.height;

          return rect1Left < rect2Right && rect1Right > rect2Left && rect1Top < rect2Bottom && rect1Bottom > rect2Top;
        }

        /**
         * Check if rect is inside of container
         * @param {{x: number, y: number, width: number, height: number}} rect
         * @param {{x: number, y: number, width: number, height: number}} container
         * @returns {boolean}
         */
        function isRectangleContained(rect, container) {
          return rect.x >= container.x &&
            rect.y >= container.y &&
            rect.x + rect.width <= container.x + container.width &&
            rect.y + rect.height <= container.y + container.height;
        }

        function onSubregionClick($event, subregion) {
          $event.stopPropagation();
          if (!subregion || $scope.loading || $scope.mode !== 'list') {
            return;
          }

          $scope.dataProvider.subregionToEdit = angular.copy(
            ($scope.dataProvider.locationStateRegionsResource.value || [])
              .find(s => s.unique_id === subregion.unique_id)
          );
        }

        function onRootClick($event) {
          $event.stopPropagation();
          if ($scope.loading || $scope.mode !== 'list') {
            return;
          }
          $scope.dimensionLines = [];
          $scope.dataProvider.subregionToEdit = undefined;
        }

        function onRoomClick($event) {
          $event.stopPropagation();
          if ($scope.loading || $scope.mode !== 'list') {
            return;
          }
          $scope.dataProvider.subregionToEdit = undefined;
          processDimensionLines(true);

        }

        /**
         * Draw dimension lines for selected subregion or room
         * @param {boolean} drawRoom draw only room dimensions
         */
        function processDimensionLines(drawRoom) {
          if ($scope.mode === 'room') {
            drawRoom = true;
          }

          const subregion = $scope.selectedSubregion;
          const room = $scope.roomBoundaries;
          const fov = $scope.fov;

          if (!!subregion && !!room && !!fov && !drawRoom) {
            // Drawing dimension lines for subregion
            if ($scope.mountingMethod === 'wall') {// wall mounting
              $scope.dimensionLines = [
                ...getDimensionLines(subregion, undefined, fov.height, 1 / 2), // to the Radar wall
                ...getDimensionLines(subregion, fov.width / 2, undefined, 2 / 3), // to the Radar axis.
                ...getDimensionLines(subregion, undefined, room.y, 2 / 3), // to the top room edge
                ...getDimensionLines(subregion, room.x, undefined, 1 / 3), // to the left room edge
                ...getDimensionLines(subregion, room.x + room.width, undefined, 1 / 3), // to the right room edge
              ];
            } else if ($scope.mountingMethod === 'ceiling') { // ceiling mounting
              $scope.dimensionLines = [
                ...getDimensionLines(subregion, undefined, fov.height / 2, 1 / 3), // to the Radar Ox axis
                ...getDimensionLines(subregion, fov.width / 2, undefined, 1 / 3), // to the Radar Oy axis
                ...getDimensionLines(subregion, undefined, room.y, 2 / 3), // to the top room edge
                ...getDimensionLines(subregion, undefined, room.y + room.height, 2 / 3), // to the bottom room edge
                ...getDimensionLines(subregion, room.x, undefined, 2 / 3), // to the left room edge
                ...getDimensionLines(subregion, room.x + room.width, undefined, 2 / 3), // to the right room edge
              ];
            } else if ($scope.mountingMethod === 'corner') { // corner mounting
              $scope.dimensionLines = [
                ...getDimensionLines(subregion, undefined, room.y + room.height, 2 / 3), // to the bottom room edge
                ...getDimensionLines(subregion, room.x + room.width, undefined, 2 / 3), // to the right room edge
                ...getDimensionLines(subregion, undefined, room.y, 2 / 3), // to the top room edge
                ...getDimensionLines(subregion, room.x, undefined, 2 / 3), // to the left room edge
              ];
            } else {
              $scope.dimensionLines = [];
            }

            // Height and width
            $scope.dimensionLines = $scope.dimensionLines.concat([
              createDimensionLine(
                subregion.x,
                subregion.y + subregion.height * (subregion.y > 30 ? 0 : 1),
                subregion.x + subregion.width,
                subregion.y + subregion.height * (subregion.y > 30 ? 0 : 1)
              ), // subregion width
              createDimensionLine(
                subregion.x + subregion.width * (subregion.x > 30 ? 0 : 1),
                subregion.y,
                subregion.x + subregion.width * (subregion.x > 30 ? 0 : 1),
                subregion.y + subregion.height
              ), // subregion height
            ]);

          } else if (!!room && !!fov && drawRoom) { // room dimensions

            $scope.dimensionLines = [
              createDimensionLine(
                room.x,
                room.y + room.height * (room.y > 30 ? 0 : 1),
                room.x + room.width,
                room.y + room.height * (room.y > 30 ? 0 : 1),
              ), // room width
              createDimensionLine(
                room.x + room.width * (room.x > 30 ? 0 : 1),
                room.y,
                room.x + room.width * (room.x > 30 ? 0 : 1),
                room.y + room.height
              ), // room height
            ];

            if ($scope.mountingMethod === 'wall') {
              // wall mounting
              $scope.dimensionLines = [
                ...$scope.dimensionLines,
                // From radar Oy axis to left side of the room
                createDimensionLine(room.x, fov.height * 1 / 3, fov.width / 2, fov.height * 1 / 3, 'X₁='),
                // From radar Oy axis to right side of the room
                createDimensionLine(fov.width / 2, fov.height * 2 / 3, room.x + room.width, fov.height * 2 / 3, 'X₂='),

                // From radar to bottom side of the room
                createDimensionLine(fov.width * 2 / 3, fov.height, fov.width * 2 / 3, room.y + room.height, 'Y₁='),
                // From radar to top side of the room
                createDimensionLine(fov.width / 4, fov.height, fov.width / 4, room.y, 'Y₂='),
              ];
            } else if ($scope.mountingMethod === 'ceiling') {
              // ceiling mounting
              $scope.dimensionLines = [
                ...$scope.dimensionLines,
                ...getDimensionLines(room, undefined, fov.height / 2, 1 / 2, 'Y₁=', 'Y₂='), // to the Radar Ox axis
                ...getDimensionLines(room, fov.width / 2, undefined, 1 / 2, 'X₁=', 'X₂='), // to the Radar Oy axis
              ];
            } else if ($scope.mountingMethod === 'corner') {
              // corner mounting
              $scope.dimensionLines = [
                createDimensionLine(
                  room.x,
                  room.y + room.height * (room.y > 30 ? 0 : 1),
                  room.x + room.width,
                  room.y + room.height * (room.y > 30 ? 0 : 1),
                  'X₂='
                ), // room width
                createDimensionLine(
                  room.x + room.width * (room.x > 30 ? 0 : 1),
                  room.y,
                  room.x + room.width * (room.x > 30 ? 0 : 1),
                  room.y + room.height,
                  'Y₂='
                ), // room height
              ];
            } else {
              $scope.dimensionLines = [];
            }

          } else {
            $scope.dimensionLines = [];
            return;
          }

          // Hide short and duplicate lines
          $scope.dimensionLines.forEach((dimensionLine, i, arr) => {
            if (
              dimensionLine.length < 29 || // Line too short
              arr // or line is duplicated (another parallel line with the same length)
                .filter((dl, j) => j < i)
                .some((dl) => dl.id !== dimensionLine.id &&
                  dl.length === dimensionLine.length && (
                    (dl.y1 === dimensionLine.y1 && dl.y2 === dimensionLine.y2) ||
                    (dl.y1 === dimensionLine.y2 && dl.y2 === dimensionLine.y1) ||
                    (dl.x1 === dimensionLine.x1 && dl.x2 === dimensionLine.x2) ||
                    (dl.x1 === dimensionLine.x2 && dl.x2 === dimensionLine.x1)
                  )
                )
            ) {
              dimensionLine.hidden = true;
            }
          });

        }

        /**
         * Get dimension line between rectangle and some vertical/horizontal line. Set x or y to define it
         * @param {{x: number, y: number, width: number, height: number}} rect
         * @param {number | undefined} x If x is set, then the destination line is vertical
         * @param {number | undefined} y If y is set, then the destination line is horizontal
         * @param {number} [shift] At what distance to place the line, in fractions of the width of the face where the
         *  Dimensional Line begins. 1/2 for the middle.
         * @param {string} [labelPrefix1] optional label prefix
         * @param {string} [labelPrefix2] optional label prefix
         * @return {{id: string, x1: number, y1: number, x2: number, y2: number, label: string, length: number, [hidden]: boolean}}
         */
        function getDimensionLines(rect, x, y, shift = 1 / 2, labelPrefix1 = '', labelPrefix2 = '',) {
          let lines = [];

          if (x != null) { // destination is vertical line

            if (rect.x + rect.width <= x) { // The rectangle is on the left
              // draw dimension line from right side of rectangle
              lines = [
                createDimensionLine(rect.x + rect.width, rect.y + rect.height * shift, x, rect.y + rect.height * shift, labelPrefix1)
              ];
            } else if (rect.x >= x) { // The rectangle is on the right.
              // draw dimension line from left side of rectangle
              lines = [
                createDimensionLine(x, rect.y + rect.height * shift, rect.x, rect.y + rect.height * shift, labelPrefix1)
              ];
            } else { // The rectangle intersects the line.
              // draw dimension line from both sides of rectangle
              lines = [
                createDimensionLine(rect.x, rect.y + rect.height * shift, x, rect.y + rect.height * shift, labelPrefix1),
                createDimensionLine(x, rect.y + rect.height * shift, rect.x + rect.width, rect.y + rect.height * shift, labelPrefix2)
              ];
            }

          } else if (y != null) { // destination is horizontal line

            if (rect.y + rect.height <= y) { // The rectangle is above the line
              // draw dimension line from bottom side of rectangle
              lines = [
                createDimensionLine(rect.x + rect.width * shift, y, rect.x + rect.width * shift, rect.y + rect.height, labelPrefix1)
              ];
            } else if (rect.y >= y) { // The rectangle is below the line.
              // draw dimension line from top side of rectangle
              lines = [
                createDimensionLine(rect.x + rect.width * shift, rect.y, rect.x + rect.width * shift, y, labelPrefix1)
              ];
            } else { // The rectangle intersects the line.
              // draw dimension line from both sides of rectangle
              lines = [
                createDimensionLine(rect.x + rect.width * shift, rect.y + rect.height, rect.x + rect.width * shift, y, labelPrefix1),
                createDimensionLine(rect.x + rect.width * shift, y, rect.x + rect.width * shift, rect.y, labelPrefix2)
              ];
            }

          }

          return lines.filter(l => l.length > 0);
        }

        function createDimensionLine(x1, y1, x2, y2, labelPrefix = '') {
          const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

          const line = {
            id: 'dimensionLine_' + idIncrementor++,
            x1: x1,
            y1: y1,
            x2: x2,
            y2: y2,
            label: labelPrefix + formatMeasurements(length),
            length: length
          };

          if (x1 > x2 || y1 < y2) {
            // NOTE: You should always draw lines from left to right (from small x to large x)
            //  and from bottom to top (from large y to small y).
            //  Or the line labels will be upside down. It's an SVG bug
            line.x1 = x2;
            line.y1 = y2;
            line.x2 = x1;
            line.y2 = y1;
          }

          return line;
        }

        /**
         * Format measurement units to user preferred
         * @param {number} measurement in centimeters
         * @return {string} measurement in preferred user units (meter, feet, etc.)
         */
        function formatMeasurements(measurement) {
          if ($scope.systemOfMeasurement === 'metric') {
            return Math.round(measurement * 100) / 100 + ' cm';
          } else if ($scope.systemOfMeasurement === 'us') {
            return utils.convertMetersToInches(measurement / 100, 2) + '"'; // from centimeters to inches
          }
          return Math.round(measurement * 100) / 100 + '';
        }

        function onSubregionMouseDown($event, subregion) {
          if (isDragging) {
            // cancel dragging
            $scope.selectedSubregion.x = selectedSubregionOriginalX;
            $scope.selectedSubregion.y = selectedSubregionOriginalY;
            isDragging = false;
            return;
          }

          if (!subregion || $scope.loading || $scope.mode !== 'subregion' || !$scope.selectedSubregion || subregion !== $scope.selectedSubregion) {
            return;
          }

          isDragging = true;
          dndOriginalX = $event.pageX;
          dndOriginalY = $event.pageY;
          selectedSubregionOriginalX = $scope.selectedSubregion.x;
          selectedSubregionOriginalY = $scope.selectedSubregion.y;
        }

        function onMouseUp($event) {
          if (!isDragging) {
            return;
          }
          isDragging = false;
          onSubregionMove($event);
        }

        function onMouseMove($event) {
          if (!isDragging) {
            return;
          }
          onSubregionMove($event);
        }

        function onSubregionMove($event) {
          $scope.$applyAsync(() => {
            let newX = Math.round(selectedSubregionOriginalX + ($event.pageX - dndOriginalX) / $scope.rootFontSize);
            let newY = Math.round(selectedSubregionOriginalY + ($event.pageY - dndOriginalY) / $scope.rootFontSize);

            if (newX < 0) {
              $scope.selectedSubregion.x = 0;
            } else if (newX > $scope.fovWidth - $scope.selectedSubregion.width) {
              $scope.selectedSubregion.x = $scope.fovWidth - $scope.selectedSubregion.width;
            } else {
              $scope.selectedSubregion.x = newX;
            }

            if (newY < 0) {
              $scope.selectedSubregion.y = 0;
            } else if (newY > $scope.fovHeight - $scope.selectedSubregion.height) {
              $scope.selectedSubregion.y = $scope.fovHeight - $scope.selectedSubregion.height;
            } else {
              $scope.selectedSubregion.y = newY;
            }

            validateSubregion($scope.selectedSubregion);
            processDimensionLines();

            subregionChangeDebounced();
          });
        }

        const subregionChangeDebounced = utils.debounce(() => {
          $scope.$applyAsync(() => {
            const subregion = $scope.selectedSubregion;
            if (!!$scope.onSubregionChange && !!subregion) {
              const radarOrigin = getRadarOrigin();

              // Transform subregion corners from schema coordinates to radar coordinates
              const topLeftX = (subregion.x - radarOrigin.x) * radarOrigin.multiplierX;
              const topLeftY = (subregion.y - radarOrigin.y) * radarOrigin.multiplierY;
              const bottomRightX = (subregion.x + subregion.width - radarOrigin.x) * radarOrigin.multiplierX;
              const bottomRightY = (subregion.y + subregion.height - radarOrigin.y) * radarOrigin.multiplierY;

              $scope.onSubregionChange({
                subregionCoords: {
                  // Calculate min/max coordinates in device coordinate system
                  xMin: Math.min(topLeftX, bottomRightX) / 100,
                  xMax: Math.max(topLeftX, bottomRightX) / 100,
                  yMin: Math.min(topLeftY, bottomRightY) / 100,
                  yMax: Math.max(topLeftY, bottomRightY) / 100,
                  width: Math.abs(bottomRightX - topLeftX) / 100,
                  height: Math.abs(bottomRightY - topLeftY) / 100
                }
              });
            }
          });
        }, 5);

      }
    };
  })
  .name;
